import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogContent, DialogActions,
    Button, TextField, Box, Typography
} from '@mui/material';
import ConfirmationDialog from './ConfirmationDialog.comp';

/**
 * AssignLicensesDialog — "Assign Licenses to (School Name)", after the pop-up in the
 * P1 Workspace Flow deck (slide 106).
 *
 * Network mode (`fromNetworkPool`): a network hands licenses to one of its schools out
 * of the pool its partner assigned it. It shows the licenses left in that pool, never
 * DQ Tokens. Its layout follows the SNW-ALL-My Schools-AssignLicense mockup (bug sheet
 * Ian-SNW-29Sep-6): the school's Total / Used / Remaining, "How many licenses to
 * assign", what is left in the network pool, then a "Confirm this assignment" step.
 */
const ORANGE = '#FF6900';
const TEAL = '#0097b2';

const pillBtn = { borderRadius: '20px', minWidth: '110px', fontWeight: 'bold', letterSpacing: '0.5px', textTransform: 'none' };

const AssignLicensesDialog = ({
    open,
    onClose,
    onSubmit,
    loading,
    orgName,
    orgCode,
    sectionName,
    sectionCode,
    sectionLicenseStats,
    sectionLicenseStatsLoading = false,
    isSchool = false,
    // DQ Tokens redemption — same figures already shown in the page-level
    // strip (DQProClientList.page.jsx), just surfaced here too so the token
    // math is visible right where the owner is choosing a license count.
    // Omit/leave undefined to render the dialog exactly as before.
    tokenBalance = null,
    tokensPerLicense = null,
    onBuyMoreTokens,
    // Network mode: licenses left in the network's own pool (null while loading or
    // unknown — the server still refuses more than the pool holds).
    fromNetworkPool = false,
    poolAvailable = null,
    // Network mode: the school's own figures, for the three cards.
    targetTotal = 0,
    targetUsed = 0,
}) => {
    const isClassPool = Boolean(sectionCode);
    const orgLabel = isSchool || fromNetworkPool ? 'school' : 'client';
    const [licenseCount, setLicenseCount] = useState('');
    const [error, setError] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);

    const hasTokenInfo = !fromNetworkPool && tokenBalance !== null && Number(tokensPerLicense) > 0;
    const redeemableLicenses = hasTokenInfo ? Math.floor(tokenBalance / tokensPerLicense) : 0;
    const enteredCount = Number(licenseCount);
    const tokenCost = hasTokenInfo && enteredCount > 0 ? enteredCount * tokensPerLicense : 0;
    const tokensRemaining = hasTokenInfo ? tokenBalance - tokenCost : 0;
    const poolKnown = fromNetworkPool && typeof poolAvailable === 'number';

    useEffect(() => {
        if (open) {
            const total = Number(sectionLicenseStats?.total);
            setLicenseCount(Number.isFinite(total) && total > 0 ? String(total) : '');
            setError('');
            setShowConfirm(false);
        }
    }, [open, sectionLicenseStats]);

    const handleAssign = () => {
        if (!licenseCount || isNaN(Number(licenseCount)) || Number(licenseCount) <= 0) {
            setError('Enter a valid number of licenses');
            return;
        }
        if (poolKnown && enteredCount > poolAvailable) {
            setError(`Your network has ${poolAvailable.toLocaleString()} license${poolAvailable === 1 ? '' : 's'} available`);
            return;
        }
        setError('');
        setShowConfirm(true);
    };

    const submit = () => {
        setShowConfirm(false);
        const payload = { org_code: orgCode, license_count: Number(licenseCount) };
        if (isClassPool) {
            payload.section_code = sectionCode;
        }
        onSubmit(payload);
    };

    const assignTargetName = isClassPool ? (sectionName || sectionCode) : orgName;
    const targetWord = isClassPool ? 'class' : orgLabel;
    const used = Number(sectionLicenseStats?.used) || 0;
    const total = Number(sectionLicenseStats?.total) || 0;
    const hasClassPool = isClassPool && total > 0;
    const enrolledNoPool = isClassPool && total === 0 && used > 0;
    const bodyText = { textAlign: 'center', fontSize: '14px', color: '#000', lineHeight: 1.5 };

    return (
        <>
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
                PaperProps={{ sx: { borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.18)', py: 1.5 } }}>
            <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, pt: '16px !important', pb: 1 }}>
                {fromNetworkPool ? (
                    <>
                        <Typography component="h2" sx={{ color: 'var(--brand-text, #111827)', fontWeight: 'bold', textAlign: 'center', fontSize: '18px', mb: 1 }}>
                            Assign licenses to {assignTargetName}
                        </Typography>
                        <Typography sx={{ textAlign: 'center', fontSize: '13px', color: 'var(--brand-text-muted, #6B7280)', lineHeight: 1.5, mb: 2 }}>
                            Give this school a share of your network pool. Assigned licenses count as consumed from the
                            network pool straight away.
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '10px', mb: 2 }}>
                            {[
                                ['Total licenses', Number(targetTotal) || 0, false],
                                ['Used licenses', Number(targetUsed) || 0, false],
                                ['Remaining licenses', Math.max(0, (Number(targetTotal) || 0) - (Number(targetUsed) || 0)), true],
                            ].map(([label, value, accent], i) => (
                                <Box key={label} sx={{ p: '10px 12px', borderLeft: i ? '1px solid var(--brand-border, #E5E7EB)' : 'none' }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--brand-text-muted, #6B7280)' }}>{label}</Typography>
                                    <Typography sx={{ fontSize: '18px', fontWeight: 800, color: accent ? ORANGE : 'var(--brand-text, #111827)' }}>{value.toLocaleString()}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 0.75 }}>
                            How many licenses to assign
                        </Typography>
                        <TextField
                            type="number"
                            value={licenseCount}
                            onChange={(e) => { setLicenseCount(e.target.value); if (error) setError(''); }}
                            error={!!error}
                            helperText={error || ' '}
                            fullWidth
                            autoFocus
                            size="small"
                            inputProps={{ min: 1, 'aria-label': 'How many licenses to assign', ...(poolKnown ? { max: poolAvailable } : {}) }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', '& fieldset': { borderColor: ORANGE }, '&.Mui-focused fieldset': { borderColor: ORANGE } } }}
                        />
                        <Typography sx={{ fontSize: '12.5px', color: 'var(--brand-text-muted, #6B7280)' }}>
                            {poolKnown
                                ? `${poolAvailable.toLocaleString()} license${poolAvailable === 1 ? ' is' : 's are'} still unassigned in your network pool.`
                                : 'Checking your network pool…'}
                        </Typography>
                    </>
                ) : (
                    <>
                    <Typography component="h2" sx={{ color: '#000', fontWeight: 'bold', textAlign: 'center', fontSize: '18px', mb: 2 }}>
                        Assign Licenses to <Box component="span" sx={{ color: ORANGE }}>{assignTargetName}</Box>
                    </Typography>
                    <Typography sx={{ ...bodyText, mb: 1.5 }}>
                        Input the number of licenses you wish to assign to this {targetWord}.
                    </Typography>
                    <Typography sx={{ ...bodyText, mb: 2.5 }}>
                        <strong>Please double check before clicking ASSIGN.</strong>{' '}
                        Once assigned, your {targetWord} will be able to access these licenses and use the
                        assessments you have linked to their account.
                    </Typography>
                    {hasTokenInfo && (
                        <Box sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
                            gap: 1, mb: 2, p: 1.25, borderRadius: '10px',
                            backgroundColor: 'var(--brand-bg, #FFF9F5)', border: '1px solid rgba(241, 90, 36, 0.18)',
                        }}>
                            <Typography sx={{ fontSize: '12.5px', color: 'var(--brand-text, #444)', lineHeight: 1.4 }}>
                                <strong style={{ color: ORANGE }}>{tokenBalance.toLocaleString()} DQ Tokens</strong>
                                {' → '}
                                <strong>{redeemableLicenses.toLocaleString()} licenses</strong>
                            </Typography>
                            {onBuyMoreTokens && (
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={onBuyMoreTokens}
                                    sx={{ color: ORANGE, textTransform: 'none', fontWeight: 700, fontSize: '12.5px', minWidth: 0, p: 0 }}
                                >
                                    + Buy more
                                </Button>
                            )}
                        </Box>
                    )}
                    {isClassPool && sectionLicenseStatsLoading ? (
                        <Typography sx={{ textAlign: 'center', fontSize: '13px', color: '#666', mb: 1.5 }}>
                            Loading current class allocation…
                        </Typography>
                    ) : null}
                    {!sectionLicenseStatsLoading && hasClassPool ? (
                        <Typography sx={{ textAlign: 'center', fontSize: '13px', color: '#666', mb: 1.5 }}>
                            Current class pool: {used} used / {Math.max(0, total - used)} available ({total} total)
                        </Typography>
                    ) : null}
                    {!sectionLicenseStatsLoading && enrolledNoPool ? (
                        <Typography sx={{ textAlign: 'center', fontSize: '13px', color: '#666', mb: 1.5 }}>
                            No class pool set yet ({used} student{used === 1 ? '' : 's'} enrolled). Enter a total below to
                            allocate seats to this class.
                        </Typography>
                    ) : null}
                    <TextField
                        label="Number of Licenses"
                        type="number"
                        value={licenseCount}
                        onChange={e => { setLicenseCount(e.target.value); if (error) setError(''); }}
                        error={!!error}
                        helperText={error || (hasTokenInfo && enteredCount > 0
                            ? `Costs ${tokenCost.toLocaleString()} DQ Tokens — ${tokensRemaining.toLocaleString()} left after this`
                            : ' ')}
                        fullWidth
                        autoFocus
                        size="small"
                        inputProps={{ min: 1, ...(poolKnown ? { max: poolAvailable } : {}) }}
                        sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: '6px', '&.Mui-focused fieldset': { borderColor: ORANGE } },
                            '& .MuiInputLabel-root.Mui-focused': { color: ORANGE },
                        }}
                    />
                    {fromNetworkPool && (
                        <Typography sx={{ textAlign: 'center', fontSize: '13px', color: '#666', mt: 0.5 }}>
                            {poolKnown ? (
                                <>
                                    Licenses available: <strong style={{ color: ORANGE }}>{poolAvailable.toLocaleString()}</strong>
                                    {enteredCount > 0 && enteredCount <= poolAvailable
                                        ? ` · ${(poolAvailable - enteredCount).toLocaleString()} left after this`
                                        : ''}
                                </>
                            ) : 'Checking your network\'s licenses…'}
                        </Typography>
                    )}
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, justifyContent: 'center', gap: 1.5 }}>
                <Button onClick={onClose} variant="outlined"
                        sx={{ ...pillBtn, borderColor: TEAL, color: TEAL, '&:hover': { borderColor: TEAL } }}>
                    {fromNetworkPool ? 'Cancel' : 'CANCEL'}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleAssign}
                    sx={{ ...pillBtn, backgroundColor: ORANGE, color: '#fff', '&:hover': { backgroundColor: '#e55e00' } }}
                    disabled={loading || (poolKnown && poolAvailable === 0)}
                >
                    {loading ? (fromNetworkPool ? 'Assigning…' : 'ASSIGNING…') : (fromNetworkPool ? 'Assign' : 'ASSIGN')}
                </Button>
            </DialogActions>
        </Dialog>

        <ConfirmationDialog
            open={showConfirm}
            onClose={() => setShowConfirm(false)}
            onConfirm={submit}
            title="Confirm this assignment"
            message={fromNetworkPool && poolKnown
                ? `Assign <b>${enteredCount.toLocaleString()}</b> licenses to <b>${assignTargetName}</b>, leaving <b>${Math.max(0, poolAvailable - enteredCount).toLocaleString()}</b> in your network pool? The licenses are handed over straight away and this can't be undone.`
                : hasTokenInfo
                ? `Assign <b>${enteredCount.toLocaleString()}</b> licenses to <b>${assignTargetName}</b>, using <b>${tokenCost.toLocaleString()}</b> of your <b>${tokenBalance.toLocaleString()}</b> DQ Tokens? The licenses are handed over straight away and this can't be undone.`
                : `Assign <b>${enteredCount.toLocaleString()}</b> licenses to <b>${assignTargetName}</b>? The licenses are handed over straight away and this can't be undone.`}
            confirmText="Yes, assign now"
            cancelText="Cancel"
        />
        </>
    );
};

export default AssignLicensesDialog;
